import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Budget,
  BudgetSummary as BudgetSummaryType,
  ContributionCalculationType,
} from '../types/budget';
import { formatCurrency } from '../utils/budget';
// currency is guaranteed in budget.settings now
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface BudgetSummaryProps {
  budget: Budget;
  summary: BudgetSummaryType;
  calculationType?: ContributionCalculationType;
}

export const BudgetSummary: React.FC<BudgetSummaryProps> = ({
  budget,
  summary,
  calculationType = ContributionCalculationType.INCOME_RATIO,
}) => {
  const { t } = useTranslation();

  // Map calculation type to translation key
  const getCalculationTypeLabel = () => {
    switch (calculationType) {
      case ContributionCalculationType.INCOME_RATIO:
        return t('budget.settings.incomeRatio');
      case ContributionCalculationType.EQUAL_SPLIT:
        return t('budget.settings.equalSplit');
      case ContributionCalculationType.AFTER_PERSONAL:
        return t('budget.settings.afterPersonal');
      case ContributionCalculationType.FULL_BALANCE:
        return t('budget.settings.fullBalance');
      default:
        return t('budget.settings.incomeRatio');
    }
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow dark:bg-surface-800 sm:p-6">
      <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">
        {t('budget.summary.title')}
      </h2>

      {/* Global Summary */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-surface-600 dark:bg-surface-800/50 sm:p-4">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {t('budget.summary.totalIncome')}
          </h3>
          <div className="mt-2 flex items-center gap-1">
            <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 sm:text-2xl">
              {formatCurrency(summary.totalIncome, budget.settings.currency)}
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-surface-600 dark:bg-surface-800/50 sm:p-4">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {t('budget.summary.personalExpenses')}
          </h3>
          <div className="mt-2 flex items-center gap-1">
            <p className="text-lg font-semibold text-rose-600 dark:text-rose-400 sm:text-2xl">
              {formatCurrency(summary.totalExpenses, budget.settings.currency)}
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-surface-600 dark:bg-surface-800/50 sm:p-4">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {t('budget.summary.balance')}
          </h3>
          <p
            className={`mt-2 text-lg font-semibold sm:text-2xl ${
              summary.balance >= 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-rose-600 dark:text-rose-400'
            }`}
          >
            {formatCurrency(summary.balance, budget.settings.currency)}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-surface-600 dark:bg-surface-800/50 sm:p-4">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {t('budget.summary.commonExpenses')}
          </h3>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-lg font-semibold text-slate-900 dark:text-white sm:text-2xl">
              {formatCurrency(
                summary.personContributions.common.expenses,
                budget.settings.currency
              )}
            </p>
            {/* Calculation method indicator */}
            <div className="group relative">
              <div className="flex items-center justify-center rounded-full p-1 text-slate-400 dark:text-slate-500">
                <InformationCircleIcon className="h-4 w-4" />
              </div>
              <div className="invisible absolute right-0 top-full z-10 mt-2 w-64 rounded-lg bg-white p-3 text-sm shadow-lg transition-all group-hover:visible dark:bg-surface-800">
                <p className="font-medium text-slate-900 dark:text-white">
                  {getCalculationTypeLabel()}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-surface-600 dark:bg-surface-800/50 sm:p-4">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {t('budget.summary.remaining')}
          </h3>
          <div className="mt-2">
            <p
              className={`text-lg font-semibold sm:text-2xl ${
                Object.values(summary.personContributions)
                  .filter((person) => person.availableBalance)
                  .reduce((sum, person) => sum + person.availableBalance, 0) >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-rose-600 dark:text-rose-400'
              }`}
            >
              {formatCurrency(
                Object.values(summary.personContributions)
                  .filter((person) => person.availableBalance)
                  .reduce((sum, person) => sum + person.availableBalance, 0),
                budget.settings.currency
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Individual Contributions */}
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-medium text-slate-900 dark:text-white">
          {t('budget.summary.individualContributions')}
        </h3>
        <div className="space-y-2">
          {Object.entries(summary.personContributions)
            .filter(([person]) => person !== 'common')
            .map(([person, data]) => (
              <div key={person} className="rounded-lg bg-slate-50 p-4 dark:bg-surface-700/50">
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-[100px_1fr_1fr_1fr_auto] xl:grid-cols-[100px_180px_180px_160px_160px_1fr]">
                  <h4 className="text-md font-medium text-slate-900 dark:text-white">
                    {budget.responsibles.find((responsible) => responsible.id === person)?.name}
                  </h4>

                  {/* Income */}
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-sm text-slate-500 dark:text-slate-400">
                      {t('budget.summary.income')}:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(data.income, budget.settings.currency)}
                      </span>
                    </div>
                  </div>

                  {/* Expenses */}
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-sm text-slate-500 dark:text-slate-400">
                      {t('budget.summary.expenses')}:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
                        {formatCurrency(data.expenses, budget.settings.currency)}
                      </span>
                    </div>
                  </div>

                  {/* Balance */}
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-sm text-slate-500 dark:text-slate-400">
                      {t('budget.summary.balance')}:
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        data.balance >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-rose-600 dark:text-rose-400'
                      }`}
                    >
                      {formatCurrency(data.balance, budget.settings.currency)}
                    </span>
                  </div>

                  {/* Contribution */}
                  <div className="flex items-center gap-2">
                    <span className="w-24 text-sm text-slate-500 dark:text-slate-400">
                      {t('budget.summary.contribution')}:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {formatCurrency(data.contribution, budget.settings.currency)}
                      </span>
                    </div>
                  </div>

                  {/* Available Balance - Uses media queries for positioning */}
                  <div className="flex justify-start lg:col-start-5 lg:mt-2 xl:col-start-auto xl:mt-0 xl:justify-end">
                    <div className="flex items-center gap-2 rounded-md bg-slate-100 px-2 py-1 dark:bg-surface-600/50 sm:px-3">
                      <span className="w-28 text-sm text-slate-500 dark:text-slate-400">
                        {t('budget.summary.availableBalance')}:
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          data.availableBalance >= 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-lg font-bold text-rose-600 dark:text-rose-400'
                        }`}
                      >
                        {formatCurrency(data.availableBalance, budget.settings.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
